import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, effect, inject, } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { EvCriticalAlertComponent } from "ev-critical-alert";
import { combineLatest, filter, map, take } from 'rxjs';
import { MessageCenterService } from 'src/services/message-center.service';
import { MessageCenterStore } from 'src/stores/message-center.store';
import { EvAuthorizationService } from 'src/util/auth0/ev-auth.service';
import { EVServiceType } from 'src/util/auth0/models/evServiceType.enum';
import { UiService } from 'src/util/ui/ui.service';
import { PublicRouterOutletComponent } from './public-router-outlet.component';
import { LoadingPageComponent } from './shared-components/loading-page/loading-page.component';
import { EvPriorityNewsAlertComponent } from 'ev-priority-news-alert';
import { DismissedMessagesService } from 'src/services/dismiss-messages.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        AsyncPipe,
        DatePipe,
        LoadingPageComponent,
        PublicRouterOutletComponent,
        RouterOutlet,
        EvCriticalAlertComponent,
        EvPriorityNewsAlertComponent
    ],
})
export class AppComponent {
    readonly messageCenterStore = inject(MessageCenterStore);
    private readonly messageCenterService = inject(MessageCenterService);
    private readonly dismissedMessagesService = inject(DismissedMessagesService);
    public readonly auth = inject(AuthService);
    private readonly router = inject(Router);
    private readonly uiService = inject(UiService);
    private readonly http = inject(HttpClient);
    private readonly authService = inject(EvAuthorizationService);
    private initialBuildVersion: string | null = null;

    constructor() {
    effect(() => {
        const count = this.messageCenterStore.criticalMessages().length;
        const bannerHeight = count > 0 ? count * 48 : 0; // adjust 48 to match actual banner height
        document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);
    });
}
    ngOnInit(): void {
        this.initBuildVersionCheck();

        const navStart$ = this.router.events.pipe(filter((event) => event instanceof NavigationStart), map(e => e as NavigationStart));
        const navEnd$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd), map(e => e as NavigationEnd));

        combineLatest([this.auth.isAuthenticated$, navStart$])
            .subscribe(([isAuthenticated, navStart]) => {
                const isPublicRoute = navStart.url.startsWith('/public');
                if (!isPublicRoute && !isAuthenticated) {
                    const queries = this.splitUrl(navStart.url);

                    const domainHintParam = queries.find(str => str.includes('domain_hint'));
                    const domain_hint = (domainHintParam || '').split('=')[1];

                    const loginHintParam = queries.find(str => str.includes('login_hint'));
                    const login_hint = (loginHintParam || '').split('=')[1];

                    this.auth.loginWithRedirect({
                        appState: {
                            target: navStart.url,
                        },
                        authorizationParams: {
                            domain_hint,
                            login_hint,
                        },
                    });
                }
            });

        this.auth.isLoading$.subscribe(x => {
            if (x) {
                this.uiService.stopGlobalSpinner();
            }
            else {
                this.uiService.startGlobalSpinner();
            }
        })

        navEnd$
            .pipe(take(1))
            .subscribe(_ => {
                this.uiService.stopGlobalSpinner();
            });
    }

    onDismissPriorityMessage(messageId: string) {
        this.dismissedMessagesService.dismiss(messageId);
    }

    private initBuildVersionCheck(): void {
        this.http.get<any>(`assets/buildversion.json?t=${Date.now()}`).pipe(take(1)).subscribe(v => {
            this.initialBuildVersion = v.buildNumber;
            this.authService.hasPermission(EVServiceType.Internal_Debug).pipe(
                filter(hasPermission => hasPermission),
                take(1)
            ).subscribe(() => console.log('Build Version:', v));
        });
        this.scheduleVersionCheck ();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkAndReload();
            }
        });
    }

    private fetchBuildVersion() {
        return this.http.get<any>(`assets/buildversion.json?t=${Date.now()}`).pipe(
            map(v => v.buildNumber)
        );
    }

    private checkAndReload(): void {
        this.fetchBuildVersion().pipe(take(1)).subscribe(v => {
            if (this.initialBuildVersion && v !== this.initialBuildVersion) {
                window.location.reload();
            }
        });
    }

    private scheduleVersionCheck (): void {
        setTimeout(() => {
            this.checkAndReload();
            this.scheduleVersionCheck ();
        }, 15 * 60 * 1000); // Every 15 minutes
    }

    splitUrl(url: string) {
        return url.includes('&') ? url.split('&') : url.split('?');
    }
}
